import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import shallow from 'zustand/shallow'

import { CobrandingLogo } from '@frame/ui-components/cobranding-logo'
import { Footer } from '@frame/ui-components/footer'
import { LoadingSpinner } from '@frame/ui-components/loading-spinner'
import { BaseWidgetHeader, WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { Body } from '@frame/ui-primitives/body'
import { Button } from '@frame/ui-primitives/button'

import { ClientSessionState } from '@frame/lib/api/client-session/types'
import { FeatureFlagList } from '@frame/lib/api/common/types'
import { InternalEventType } from '@frame/lib/api/events/types'
import { useRootContext } from '@frame/lib/context'
import { useAutoNavigate } from '@frame/lib/hooks/useAutoNavigate'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useFeatureEnabled } from '@frame/lib/hooks/useFeatures'
import { useLoginItems } from '@frame/lib/hooks/useLoginItems'
import { useStrategies } from '@frame/lib/hooks/useStrategies'
import { useStore } from '@frame/lib/stores/useStore'
import { getVerifierDisplayName } from '@frame/lib/utils/session'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'

import { ConsentCard } from './consent-card'
import { ConsentHeading } from './consent-heading'

export function Consent() {
  const { redirectToSessionStateRoute } = useAutoNavigate()
  const navigate = useNavigate()
  const { handleEvent } = useEventHandler()

  const {
    tasks,
    verifierName,
    hasExternalTasks,
    isMultiEmployer,
    targetFirstName,
    consumerInitiated,
    tenantPropertyName,
    verifierType,
    skipApplicantStatedInfo,
  } = useStore(
    (state) => ({
      tasks: state.session?.tasks ?? [],
      verifierName: state.session?.originating_party_display_name,
      hasExternalTasks: Boolean(state.session?.tasks.some((e) => e.is_external_employment)),
      isMultiEmployer: Boolean(state.session?.max_employments && state.session?.max_employments > 1),
      targetFirstName: state.session?.target_first_name,
      consumerInitiated: Boolean(state.session?.consumer_initiated),
      tenantPropertyName: state.session?.configuration?.cobranding?.tenant_property?.property_name,
      verifierType: state.session?.verifier_type,
      skipApplicantStatedInfo: state.session?.configuration.skip_applicant_stated_info ?? false,
    }),
    shallow
  )

  const loginItemIds = useLoginItems()
  const { instantEnabled, bilEnabledStrategies, credentialsEnabled, bankEnabled } = useStrategies()
  const { api, actions } = useRootContext()
  const [loading, setLoading] = useState(false)
  const bankAndPosEnabled = useFeatureEnabled(FeatureFlagList.BankPOS)

  const employerName = tasks[0]?.employer_name
  const shouldLoginToEntity = loginItemIds.length === 1
  const skipEmployersScreen =
    tasks.length === 0 &&
    consumerInitiated &&
    bilEnabledStrategies.length === 1 &&
    credentialsEnabled &&
    !instantEnabled

  const displayName = getVerifierDisplayName(verifierType, verifierName, tenantPropertyName)

  const shouldFakeWaitingForInstant = hasExternalTasks && isMultiEmployer && instantEnabled && !bankAndPosEnabled

  useEffect(() => {
    redirectToSessionStateRoute()
    handleEvent(InternalEventType.CONSENT_VIEWED)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const gatherConsent = useCallback(async () => {
    if (shouldFakeWaitingForInstant) {
      navigate(routes.fakeWaitingForInstant, { replace: true })
      return
    }
    await actions.gatherSessionConsent()
  }, [actions, hasExternalTasks, instantEnabled, isMultiEmployer, navigate])

  const handleClickConsent = useCallback(async () => {
    handleEvent(InternalEventType.CONSENT_PROVIDED)

    setLoading(true)
    const { data: session } = await api.clientSession.getClientSession()
    useStore.setState({ session })

    if (session?.state !== ClientSessionState.CONSENT) {
      redirectToSessionStateRoute()
    } else if (bankEnabled) {
      await actions.gatherSessionConsent()
    } else if (shouldLoginToEntity) {
      await actions.gatherSessionConsent()
      navigate(routes.statedInformation, { replace: true })
    } else if (skipEmployersScreen) {
      await actions.gatherSessionConsent()
      const route = skipApplicantStatedInfo ? routes.companySearch : routes.statedInformation
      navigate(route, { replace: true, state: { fromConsent: true } })
    } else {
      gatherConsent()
    }
  }, [
    skipEmployersScreen,
    api,
    actions,
    shouldLoginToEntity,
    skipApplicantStatedInfo,
    bankEnabled,
    navigate,
    handleEvent,
    gatherConsent,
    redirectToSessionStateRoute,
  ])

  return (
    <ConsentScreenContent
      employerName={employerName}
      verifierDisplayName={displayName}
      isMultiEmployer={isMultiEmployer}
      targetFirstName={targetFirstName}
      loading={loading}
      handleClickConsent={handleClickConsent}
    />
  )
}

type ConsentContentProps = {
  employerName: string | null
  verifierDisplayName: string
  isMultiEmployer: boolean
  targetFirstName?: string | null
  loading: boolean
  withNavigation?: boolean
  handleClickConsent: () => Promise<void>
}

export function ConsentScreenContent({
  employerName,
  verifierDisplayName,
  isMultiEmployer,
  targetFirstName,
  loading,
  withNavigation = true,
  handleClickConsent,
}: ConsentContentProps & {
  children?: React.ReactNode
}) {
  return (
    <>
      {withNavigation ? (
        <WidgetHeaderWithNavigation backButton={{ visible: false }} />
      ) : (
        <BaseWidgetHeader backButton={{ visible: false }} cancelButton={{ visible: false }} />
      )}
      <Body data-cy={DataAttribute.SCREEN_CONSENT} paddingTopOverride={0} className="flex flex-col items-center p-5">
        <CobrandingLogo />
        <ConsentHeading
          employerName={employerName}
          verifierName={verifierDisplayName}
          isMultiEmployer={isMultiEmployer}
          targetFirstName={targetFirstName}
        />
        <ConsentCard />
      </Body>
      <Footer
        noBottomPadding
        showDisclaimer
        showPoweredByTruework={false}
        buttons={{
          primary: (
            <Button
              data-cy={DataAttribute.BTN_CONSENT}
              disabled={loading}
              onClick={handleClickConsent}
              Icon={loading ? LoadingSpinner : undefined}
              applyCobranding
            >
              Continue
            </Button>
          ),
        }}
      />
    </>
  )
}
